// src/components/education/periodic-table/Element.tsx
// A component for rendering an element in the periodic table
import React from 'react';
import styled from 'styled-components';

// Types
export interface ElementData {
  name: string;
  symbol: string;
  number: number;
  category: string;
  xpos: number;
  ypos: number;
  phase?: string;
  atomic_mass?: number;
  electron_configuration?: string;
  electron_configuration_semantic?: string;
  electron_affinity?: number;
  electronegativity_pauling?: number;
  ionization_energies?: number[];
  image?: {
    main?: string;
    [key: string]: string | undefined;
  };
  summary?: string;
  source?: string;
}

interface ElementProps {
  data: ElementData;
  isHighlighted: boolean;
  onClick: () => void;
}

// Styled components
interface StyledElementProps {
  category: string;
  isHighlighted: boolean;
}

const ElementContainer = styled.div<StyledElementProps>`
  width: 100%;
  aspect-ratio: 1/1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: ${props => getElementColor(props.category)};
  transform: ${props => props.isHighlighted ? 'scale(1.25)' : 'scale(1)'};
  box-shadow: ${props => props.isHighlighted ? '0 0 15px rgba(255, 255, 255, 0.6)' : 'none'};
  position: relative;
  overflow: hidden;
  z-index: ${props => props.isHighlighted ? 3 : 1};
  
  &:hover {
    transform: ${props => props.isHighlighted ? 'scale(1.25)' : 'scale(1.05)'};
    z-index: 2;
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    z-index: 1;
  }
`;

const ElementNumber = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  z-index: 2;
`;

const ElementSymbol = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
  z-index: 2;
`;

const ElementName = styled.div`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 90%;
  z-index: 2;
`;

const ElementMass = styled.div`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.7);
  z-index: 2;
`;

// Helper function to get color based on element category
const getElementColor = (category: string): string => {
  const colors: Record<string, string> = {
    'noble gas': '#5662E9', // blue
    'alkali metal': '#F1383E', // red
    'alkaline earth metal': '#FF6D00', // orange
    'transition metal': '#FFC107', // amber
    'post-transition metal': '#8BC34A', // light green
    'metalloid': '#4CAF50', // green
    'nonmetal': '#009688', // teal
    'halogen': '#00BCD4', // cyan
    'lanthanoid': '#AB47BC', // purple
    'actinoid': '#7E57C2', // deep purple
    'unknown': '#9E9E9E', // grey
  };
  
  return colors[category] || '#9E9E9E';
};

// Element component
const Element: React.FC<ElementProps> = ({ data, isHighlighted, onClick }) => {
  return (
    <ElementContainer
      category={data.category}
      isHighlighted={isHighlighted}
      onClick={onClick}
    >
      <ElementNumber>{data.number}</ElementNumber>
      <ElementSymbol>{data.symbol}</ElementSymbol>
      <ElementName>{data.name}</ElementName>
      {data.atomic_mass && (
        <ElementMass>{data.atomic_mass.toFixed(2)}</ElementMass>
      )}
    </ElementContainer>
  );
};

export default Element; 